// iter45ap 4b-ii — deriveSuccessCopy
// Pure helper: given the JSON returned by GET /api/payments/status/:sid
// (post-fulfillment, per commit 4b-i), decide what the PaymentSuccess
// page should say.
//
// Backend contract recap:
//   payment_status  "paid" | "pending" | "failed" | ...
//   package_id      "single_report" | "credit_bundle_{N}" | "custom_bundle_{N}"
//   project_id      string | null                   (only single_report carries it)
//   project_paid    true | false | null             (see routes/payments.py:105-114)
//   auto_unlock_reason "already_paid" | "project_not_found" | "not_owner" | null
//   credits_added   integer (PACKAGES.credits or txn.custom_credits)
//
// Copy rules (approved by user, iter45ap):
//   Branch 1 — Auto-unlock succeeded (single + project_paid true + no reason)
//     "Your report is unlocked and ready."
//     "Nothing further needed — your report is ready to open."
//     → Open detailed report
//
//   Branch 2 — Auto-unlock declined (single + auto_unlock_reason set)
//     Three-way split so the buyer knows why $149 turned into a credit,
//     with a targeted next step per reason.
//       already_paid       → "Already unlocked" + contact email
//       project_not_found  → project silently gone (deleted / archived)
//       not_owner          → user context mismatch (rare edge)
//
//   Branch 3 — Pricing-page purchase (single + project_paid null + no reason)
//     Buyer bought a report credit from a card off the pricing page,
//     no project attached. Push them at /projects to spend it.
//
//   Branch 4 — Credit bundle top-up (credit_bundle_N)
//     N read from package_id suffix. Credit_bundle_3/5/10/20.
//
//   Branch 5 — Custom enterprise bundle (custom_bundle_N)
//     N read from credits_added because pricing is negotiated.
//
//   Branch 0 — Still polling (payment_status !== "paid")
//     Confirming payment... spinner state. No CTA.
//
//   Branch −1 — Unknown package_id (defence)
//     "Payment received." + support email so nobody's dropped in a hole.

const SUPPORT_EMAIL = "contact@futureenergyventure.com";

export function deriveSuccessCopy(status, lang = "en") {
  const x = (en, fr) => (lang === "fr" ? fr : en);
  const s = status || {};
  if (s.payment_status !== "paid") {
    return {
      headline: x("Confirming payment…", "Confirmation du paiement…"),
      subhead: x("This usually takes a few seconds.", "Cela prend généralement quelques secondes."),
      ctaLabel: null,
      ctaTarget: null,
    };
  }
  const pkgId = s.package_id || "";
  // ── single_report branches ──
  if (pkgId === "single_report") {
    // Auto-unlock succeeded.
    if (s.project_paid === true && !s.auto_unlock_reason && s.project_id) {
      return {
        headline: x("Your report is unlocked and ready.", "Votre rapport est déverrouillé et prêt."),
        subhead: x("Nothing further needed — your report is ready to open.", "Aucune autre action n'est nécessaire — votre rapport est prêt à être ouvert."),
        ctaLabel: x("Open detailed report", "Ouvrir le rapport détaillé"),
        ctaTarget: `/report/${s.project_id}`,
      };
    }
    // Auto-unlock declined — three-way split.
    if (s.auto_unlock_reason === "already_paid") {
      return {
        headline: x("1 report credit added to your wallet.", "1\u00a0crédit de rapport ajouté à votre portefeuille."),
        subhead:
          x(`This project was already unlocked before we could apply the credit — no auto-refund was possible. Contact ${SUPPORT_EMAIL} if you'd like the charge reversed, otherwise your credit is safe to spend on any other project.`, `Ce projet était déjà déverrouillé avant que nous puissions appliquer le crédit — aucun remboursement automatique n'était possible. Écrivez à ${SUPPORT_EMAIL} si vous souhaitez annuler le paiement\u00a0; sinon, votre crédit reste utilisable sur n'importe quel autre projet.`),
        ctaLabel: x("Go to my projects", "Aller à mes projets"),
        ctaTarget: "/projects",
      };
    }
    if (s.auto_unlock_reason === "project_not_found") {
      return {
        headline: x("1 report credit added to your wallet.", "1\u00a0crédit de rapport ajouté à votre portefeuille."),
        subhead:
          x("The project you started from is no longer available, so we added the credit to your wallet instead. You can spend it on any project.", "Le projet de départ n'est plus disponible\u00a0; nous avons donc ajouté le crédit à votre portefeuille. Vous pouvez l'utiliser sur n'importe quel projet."),
        ctaLabel: x("Go to my projects", "Aller à mes projets"),
        ctaTarget: "/projects",
      };
    }
    if (s.auto_unlock_reason === "not_owner") {
      return {
        headline: x("1 report credit added to your wallet.", "1\u00a0crédit de rapport ajouté à votre portefeuille."),
        subhead:
          x("We couldn't auto-unlock that project from your account, so we added the credit to your wallet. You can spend it on any project you own.", "Nous n'avons pas pu déverrouiller automatiquement ce projet depuis votre compte\u00a0; nous avons donc ajouté le crédit à votre portefeuille. Vous pouvez l'utiliser sur n'importe lequel de vos projets."),
        ctaLabel: x("Go to my projects", "Aller à mes projets"),
        ctaTarget: "/projects",
      };
    }
    // Pricing-page purchase — no project context at all.
    return {
      headline: x("1 report credit added to your wallet.", "1\u00a0crédit de rapport ajouté à votre portefeuille."),
      subhead:
        x("Head to a project to spend it — one credit unlocks one detailed report.", "Ouvrez un projet pour l'utiliser — un crédit déverrouille un rapport détaillé."),
      ctaLabel: x("Go to my projects", "Aller à mes projets"),
      ctaTarget: "/projects",
    };
  }
  // ── credit_bundle_N ──
  if (pkgId.startsWith("credit_bundle_")) {
    const n = Number(pkgId.split("_").pop()) || s.credits_added || 0;
    return {
      headline: x(`${n} report credits added to your wallet.`, `${n}\u00a0crédits de rapport ajoutés à votre portefeuille.`),
      subhead:
        x("Spend them one at a time to unlock any detailed report.", "Utilisez-les un par un pour déverrouiller n'importe quel rapport détaillé."),
      ctaLabel: x("Go to my projects", "Aller à mes projets"),
      ctaTarget: "/projects",
    };
  }
  // ── custom_bundle_N — negotiated enterprise pricing ──
  if (pkgId.startsWith("custom_bundle_")) {
    const n = s.credits_added || Number(pkgId.split("_").pop()) || 0;
    return {
      headline: x(`${n} report credits added to your wallet.`, `${n}\u00a0crédits de rapport ajoutés à votre portefeuille.`),
      subhead:
        x("Enterprise bundle — credits are available immediately. Spend them one at a time to unlock any detailed report.", "Pack entreprise — les crédits sont disponibles immédiatement. Utilisez-les un par un pour déverrouiller n'importe quel rapport détaillé."),
      ctaLabel: x("Go to my projects", "Aller à mes projets"),
      ctaTarget: "/projects",
    };
  }
  // ── unknown package_id (defence) ──
  return {
    headline: x("Payment received.", "Paiement reçu."),
    subhead:
      x(`We couldn't classify this purchase automatically. Please contact ${SUPPORT_EMAIL} and we'll sort it out.`, `Nous n'avons pas pu identifier cet achat automatiquement. Écrivez à ${SUPPORT_EMAIL} et nous réglerons la situation.`),
    ctaLabel: x("Go to my projects", "Aller à mes projets"),
    ctaTarget: "/projects",
  };
}
